import { useState } from 'react'

import { api } from '../api/client'
import type { HoneypotAgent, HoneypotDecoy, HoneypotHit } from '../api/types'
import { CategoryBars, CountSeries } from '../components/charts'
import { Card, EmptyState, ErrorBanner, formatDateTime, Spinner, StatCard } from '../components/common'
import { DataTable, type Column } from '../components/table'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { VarianteSenzaCurl } from '../components/VarianteSenzaCurl'

/**
 * Honeypot: gli agent esca e chi ci è cascato.
 *
 * Un servizio esca non riceve nessuno di legittimo — nessuno si connette a un
 * finto RDP su una macchina che non esiste — quindi ogni connessione è, per
 * costruzione, sospetta. L'honeypot è un **agent** autonomo (come l'agent
 * d'inventario), non una capability della sonda: un'esca è una superficie
 * esposta di proposito e non deve stare nel processo che ha in casa le
 * credenziali del parco. La pagina registra gli agent, ne genera il file di
 * configurazione (col segreto, mostrato una volta) e mostra gli hit ricevuti.
 */
export default function Honeypot() {
  const dati = useAsync(() => api.honeypot(), [])

  if (dati.loading && !dati.data) return <Spinner />
  if (dati.error) return <ErrorBanner error={dati.error} />
  const d = dati.data
  if (!d) return null

  const nuovi = d.summary.new

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">
            Honeypot
            {nuovi > 0 && <span className="dob-badge">{nuovi}</span>}
          </h1>
          <p className="page__subtitle">
            {t('Agent esca autonomi espongono servizi esca per rilevare tentativi di connessione inattesi o potenzialmente ostili.')}
          </p>
        </div>
        <div className="toolbar">
          <span className={`dob-stato${d.summary.agents_active > 0 && d.summary.decoys_active > 0 ? ' dob-stato--on' : ''}`}>
            {d.summary.agents_active > 0 && d.summary.decoys_active > 0
              ? t('{n} esche in ascolto', { n: d.summary.decoys_active })
              : t('Nessuna esca in ascolto')}
          </span>
        </div>
      </header>

      <Riepilogo summary={d.summary} />
      <StatisticheHoneypot />
      <ImpostazioniNotificheHoneypot />
      <NuovoAgent onCreato={() => void dati.reload()} />
      <Agenti agents={d.agents} onCambiato={() => void dati.reload()} />
      <Hits hits={d.hits} agents={d.agents} onCambiato={() => void dati.reload()} />
    </>
  )
}

function Riepilogo({ summary }: {
  summary: { hits: number; new: number; sources: number; agents_active: number; decoys_active: number }
}) {
  return (
    <div className="venv-kpi dob-riepilogo">
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{summary.agents_active}</span>
        <span className="venv-kpi__label">{t('Agent attivi')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{summary.decoys_active}</span>
        <span className="venv-kpi__label">{t('Esche in ascolto')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className={`venv-kpi__value${summary.new > 0 ? ' venv-kpi__value--err' : ' venv-kpi__value--ok'}`}>
          {summary.new}
        </span>
        <span className="venv-kpi__label">{t('Nuovi hit')}</span>
      </div>
      <div className="venv-kpi__tile">
        <span className="venv-kpi__value">{summary.sources}</span>
        <span className="venv-kpi__label">{t('Indirizzi distinti')}</span>
      </div>
    </div>
  )
}

// ------------------------------------------------- le esche di default

const PORTE_DEFAULT: Record<string, number> = {
  ssh: 22, telnet: 23, http: 80, https: 443, ftp: 21,
  smb: 445, rdp: 3389, mysql: 3306, mssql: 1433, redis: 6379, vnc: 5900,
}
const ACCESE_DI_DEFAULT = new Set(['ssh', 'http', 'rdp', 'smb'])

function escheDefault(): HoneypotDecoy[] {
  return Object.entries(PORTE_DEFAULT).map(([service, port]) => ({
    service, port, enabled: ACCESE_DI_DEFAULT.has(service), banner: '',
  }))
}

// ------------------------------------------------- dove consegna l'agent

/**
 * Il selettore «Consegna a», identico a quello degli agent d'inventario: il
 * Control Plane o una sonda che inoltra. Le due meccaniche sono la stessa —
 * stesso relay, stesso endpoint sigillato — quindi si riusa la stessa lista di
 * destinazioni. Una sonda col ricevitore spento compare disabilitata: il
 * relay è la stessa porta d'ingresso, e va accesa dalla scheda della sonda.
 */
function SelettoreConsegna({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const destinazioni = useAsync(() => api.inventoryDeliveryTargets(), [])
  const lista = destinazioni.data ?? []
  // Un endpoint già salvato che non è più in elenco (sonda rimossa) non deve
  // sparire dalla tendina facendo sembrare scelto «Control Plane»: si mostra
  // com'è, così chi modifica vede cosa c'è davvero.
  const fuoriElenco = value !== '' && !lista.some((d) => d.value === value)

  return (
    <>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t('Il Control Plane (cloud)')}</option>
        {lista
          .filter((d) => d.value !== '')
          .map((d) => (
            <option key={d.label} value={d.value} disabled={!d.available}>
              {d.label}
            </option>
          ))}
        {fuoriElenco && <option value={value}>{value}</option>}
      </select>
      <span className="field__hint">
        {t('Dove l’agent consegna gli hit: il Control Plane (cloud) o una sonda che li inoltra. Il ritorno al Control Plane è automatico se la sonda non risponde.')}
      </span>
    </>
  )
}

// ------------------------------------------------- nuovo agent

/**
 * La registrazione di un agent honeypot dedicato.
 *
 * L'honeypot è un agent a sé, isolato dalla discovery: chi lo compromette non
 * trova le credenziali del parco. Ne esce un file di configurazione col
 * segreto — mostrato una volta — da mettere accanto al binario `censo-honeypot`.
 */
function NuovoAgent({ onCreato }: { onCreato: () => void }) {
  const [aperto, setAperto] = useState(false)
  const [nome, setNome] = useState('')
  const [endpoint, setEndpoint] = useState('')
  const [intervallo, setIntervallo] = useState(5)
  const [decoys, setDecoys] = useState<HoneypotDecoy[]>(escheDefault())
  const [ignorati, setIgnorati] = useState('')
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [creato, setCreato] = useState<HoneypotAgent | null>(null)

  async function crea() {
    setBusy(true)
    setErrore(null)
    try {
      const listaIgnorati = ignorati.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)
      const out = await api.creaHoneypotAgent({
        name: nome.trim(),
        endpoint: endpoint.trim() || null,
        interval_minutes: intervallo,
        decoys,
        ignored_sources: listaIgnorati,
      })
      setCreato(out)
      onCreato()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(false)
    }
  }

  function ricomincia() {
    setCreato(null)
    setNome('')
    setEndpoint('')
    setIntervallo(5)
    setDecoys(escheDefault())
    setIgnorati('')
  }

  if (creato?.config_file) {
    return (
      <Card title={t('Agent «{nome}» registrato', { nome: creato.name })}>
        <FileConfig agent={creato} />
        <div className="toolbar" style={{ marginTop: 12 }}>
          <button type="button" className="secondary" onClick={ricomincia}>
            {t('Registra un altro agent')}
          </button>
        </div>
      </Card>
    )
  }

  if (!aperto) {
    return (
      <Card title={t('Agent honeypot')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('Un agent Honeypot espone servizi esca (SSH, RDP, SMB…) su una macchina dedicata, isolata dalla discovery e priva delle credenziali degli asset gestiti. Registra i tentativi di connessione come Alert e li trasmette a una sonda per inoltro o direttamente al Control Plane.')}
        </p>
        <button type="button" onClick={() => setAperto(true)}>
          {t('Registra un agent honeypot')}
        </button>
      </Card>
    )
  }

  return (
    <Card title={t('Nuovo agent honeypot')}>
      <div className="grid grid--2">
        <label className="field">
          <span className="field__label">{t('Nome')}</span>
          <input value={nome} onChange={(e) => setNome(e.target.value)}
                 placeholder={t('es. esca DMZ')} />
        </label>
        <label className="field">
          <span className="field__label">{t('Consegna a')}</span>
          <SelettoreConsegna value={endpoint} onChange={setEndpoint} />
        </label>
        <label className="field">
          <span className="field__label">{t('Consegna ogni (minuti)')}</span>
          <input type="number" min={1} max={1440} value={intervallo}
                 onChange={(e) => setIntervallo(Number(e.target.value) || 5)} style={{ width: 100 }} />
        </label>
      </div>

      <TabellaEsche decoys={decoys} onCambia={setDecoys} />

      <label className="field" style={{ marginTop: 12 }}>
        <span className="field__label">{t('IP da ignorare (opzionale)')}</span>
        <textarea
          value={ignorati}
          onChange={(e) => setIgnorati(e.target.value)}
          rows={2}
          placeholder={t('es. 10.0.0.5, 192.168.1.0/24')}
        />
        <span className="field__hint">
          {t('Le connessioni da questi indirizzi non diventano hit: la sonda stessa, i sistemi di monitoraggio, gli scanner autorizzati. IP singoli o CIDR, separati da virgola o a capo.')}
        </span>
      </label>

      <div className="toolbar" style={{ marginTop: 12 }}>
        <button type="button" onClick={crea} disabled={busy || !nome.trim()}>
          {t('Registra e genera la configurazione')}
        </button>
        <button type="button" className="secondary" onClick={() => setAperto(false)}>
          {t('Annulla')}
        </button>
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}

// ------------------------------------------------- la tabella delle esche

// I servizi che cifrano subito dopo il banner: l'esca registra il tentativo di
// connessione, ma non l'utente/password (viaggiano già cifrati). Chi configura
// deve saperlo prima, non scoprirlo provando. Telnet/FTP/HTTP-Basic/SMTP invece
// le espongono in chiaro e l'esca le cattura.
const SERVIZI_CIFRATI = new Set(['ssh', 'rdp'])

function TabellaEsche({ decoys, onCambia }: {
  decoys: HoneypotDecoy[]
  onCambia: (d: HoneypotDecoy[]) => void
}) {
  function tocca(i: number, patch: Partial<HoneypotDecoy>) {
    onCambia(decoys.map((d, j) => (j === i ? { ...d, ...patch } : d)))
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('Attiva')}</th>
            <th>{t('Servizio')}</th>
            <th>{t('Porta')}</th>
            <th>{t('Banner (opzionale)')}</th>
          </tr>
        </thead>
        <tbody>
          {decoys.map((decoy, i) => (
            <tr key={decoy.service} className={decoy.enabled ? 'honey-on' : undefined}>
              <td>
                <input type="checkbox" checked={decoy.enabled}
                       aria-label={t('Attiva {servizio}', { servizio: decoy.service.toUpperCase() })}
                       onChange={(e) => tocca(i, { enabled: e.target.checked })} />
              </td>
              <td>
                <span className="mono">{decoy.service.toUpperCase()}</span>
                {SERVIZI_CIFRATI.has(decoy.service) && (
                  <span className="field__hint" style={{ display: 'block' }}>
                    {t('cattura la connessione, non le credenziali (cifrate)')}
                  </span>
                )}
              </td>
              <td>
                <input type="number" value={decoy.port} min={1} max={65535}
                       aria-label={t('Porta di {servizio}', { servizio: decoy.service.toUpperCase() })}
                       style={{ width: 90 }}
                       onChange={(e) => tocca(i, { port: Number(e.target.value) || 0 })} />
              </td>
              <td>
                <input value={decoy.banner}
                       placeholder={t('es. OpenSSH_8.2p1 Ubuntu')}
                       aria-label={t('Banner di {servizio}', { servizio: decoy.service.toUpperCase() })}
                       style={{ width: '100%' }}
                       onChange={(e) => tocca(i, { banner: e.target.value })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ------------------------------------------------- il file di configurazione

function FileConfig({ agent }: { agent: HoneypotAgent }) {
  const [copiato, setCopiato] = useState<string | null>(null)
  const [mostraFile, setMostraFile] = useState(false)
  const file = agent.config_file
  if (!file) return null

  function copia(cosa: string, testo: string) {
    navigator.clipboard?.writeText(testo)
    setCopiato(cosa)
  }

  return (
    <>
      <div className="banner banner--warn">
        {agent.install_expires_at
          ? t('Il comando vale un’ora (fino alle {ora}), e contiene il segreto di questo agent: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’esca era già installata smette di consegnare finché non la reinstalli.', {
              ora: formatDateTime(agent.install_expires_at),
            })
          : t('Il comando vale un’ora, e contiene il segreto di questo agent: è l’unica volta in cui compare. Passata la finestra si rigenera — ma rigenerare invalida il segreto vecchio, quindi se l’esca era già installata smette di consegnare finché non la reinstalli.')}
      </div>

      {/* La strada normale: un comando che scarica il binario, scrive la
          configurazione e installa il servizio che tiene l'esca in ascolto. Il
          file a mano resta sotto, per chi non può eseguire lo script. */}
      {agent.install_command && (
        <div className="field">
          <label>{t('Su Linux (root)')}</label>
          <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
            {agent.install_command}
          </pre>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button type="button"
                    onClick={() => copia('unix', agent.install_command ?? '')}>
              {copiato === 'unix' ? t('Copiato') : t('Copia il comando')}
            </button>
          </div>
          <p className="field__hint">
            {t('Scarica il binario, scrive la configurazione a 0600 e installa il servizio (systemd) che tiene l’esca in ascolto e la riavvia se cade.')}
          </p>
        </div>
      )}

      {agent.install_command_windows && (
        <div className="field">
          <label>{t('Su Windows (PowerShell come amministratore)')}</label>
          <pre className="mono" style={{ overflowX: 'auto', padding: 12, whiteSpace: 'pre' }}>
            {agent.install_command_windows}
          </pre>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button type="button" className="secondary"
                    onClick={() => copia('win', agent.install_command_windows ?? '')}>
              {copiato === 'win' ? t('Copiato') : t('Copia il comando')}
            </button>
          </div>
          <VarianteSenzaCurl comando={agent.install_command_windows_legacy} />
        </div>
      )}

      {agent.downloads && (
        <div className="field">
          <label>{t('Oppure scarica il binario e configura a mano')}</label>
          <p style={{ marginTop: 0 }}>
            {Object.entries(agent.downloads).map(([piattaforma, url]) => (
              <a key={piattaforma} href={url} style={{ marginRight: 16 }} download>
                {piattaforma.startsWith('windows') ? `${piattaforma} (.exe)` : piattaforma}
              </a>
            ))}
          </p>
        </div>
      )}

      {/* Il file a mano resta, in secondo piano: serve dove la macchina non
          raggiunge il Control Plane nemmeno per scaricare. */}
      <details style={{ marginTop: 12 }} open={mostraFile}>
        <summary style={{ cursor: 'pointer' }} onClick={() => setMostraFile(!mostraFile)}>
          {t('La macchina non raggiunge il Control Plane: metto il file a mano')}
        </summary>
        <p className="text-muted" style={{ marginTop: 8 }}>
          {t('Copia il binario {binario} sulla macchina dedicata, e questo file in {percorso} con permessi {permessi}.', {
            binario: 'censo-honeypot',
            percorso: '/etc/censo/honeypot.json',
            permessi: '600',
          })}{' '}
          {t('Tienilo acceso con un servizio di sistema (systemd, un servizio Windows): un’esca ha senso solo se è in ascolto.')}
        </p>
        <pre className="mono" style={{ overflowX: 'auto', padding: 12 }}>
          {JSON.stringify(file, null, 2)}
        </pre>
        <div className="rowactions" style={{ textAlign: 'left' }}>
          <button type="button" className="secondary"
                  onClick={() => copia('file', JSON.stringify(file, null, 2))}>
            {copiato === 'file' ? t('Copiato') : t('Copia il file')}
          </button>
        </div>
      </details>
    </>
  )
}

// ------------------------------------------------- gli agent registrati

function Agenti({ agents, onCambiato }: { agents: HoneypotAgent[]; onCambiato: () => void }) {
  const [modifica, setModifica] = useState<string | null>(null)
  const [nuovaConfig, setNuovaConfig] = useState<HoneypotAgent | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  async function rigenera(id: string) {
    setErrore(null)
    try {
      const out = await api.rigeneraHoneypotAgent(id)
      setNuovaConfig(out)
      onCambiato()
    } catch (e) { setErrore(e) }
  }

  async function revoca(id: string) {
    if (!window.confirm(t('Revocare questo agente esca? Smette subito di comunicare con CENSO; per riaverlo va installato di nuovo con una configurazione nuova. Non si può annullare.'))) return
    setErrore(null)
    try { await api.revocaHoneypotAgent(id); onCambiato() } catch (e) { setErrore(e) }
  }

  async function elimina(id: string) {
    if (!window.confirm(t('Eliminare questo agent honeypot? Il segreto smette di valere e non consegnerà più.'))) return
    setErrore(null)
    try { await api.eliminaHoneypotAgent(id); onCambiato() } catch (e) { setErrore(e) }
  }

  if (nuovaConfig?.config_file) {
    return (
      <Card title={t('Nuovo segreto per «{nome}»', { nome: nuovaConfig.name })}>
        <FileConfig agent={nuovaConfig} />
        <div className="toolbar" style={{ marginTop: 12 }}>
          <button type="button" className="secondary" onClick={() => setNuovaConfig(null)}>
            {t('Chiudi')}
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card title={`${t('Agent')} (${agents.length})`}>
      {agents.length === 0 ? (
        <EmptyState
          title={t('Nessun agent honeypot')}
          hint={t('Registra un agent, comparirà nell’elenco e inizierà a inviare i rilevamenti non appena installato.')}
        />
      ) : (
        <div className="honey-agenti">
          {agents.map((a) => (
            <div key={a.id} className="honey-agente">
              <div className="honey-agente__testa">
                <span className="honey-agente__nome">{a.name}</span>
                <StatoAgente stato={a.stato} />
              </div>
              <div className="honey-agente__meta">
                {/* Se l'agent si è fatto vivo, quante esche sono **davvero** in
                    ascolto (non solo configurate); altrimenti il conteggio
                    delle configurate. */}
                {a.last_seen
                  ? <span>{t('{listening}/{active} esche in ascolto', { listening: a.decoys_listening, active: a.decoys_active })}</span>
                  : <span>{t('{n} esche', { n: a.decoys_active })}</span>}
                <span>{t('consegna: {dove}', { dove: a.endpoint || t('Control Plane') })}</span>
                <span>
                  {a.last_seen
                    ? t('ultimo contatto {quando}', { quando: formatDateTime(a.last_seen) })
                    : t('mai visto')}
                </span>
                {a.deliveries > 0 && <span>{t('{n} consegne', { n: a.deliveries })}</span>}
                {a.last_source_ip && <span className="mono">{a.last_source_ip}</span>}
              </div>
              {/* Esche accese ma con la porta non aperta (occupata da un
                  servizio vero): il caso che «sembra a posto» e non lo è. */}
              {a.last_seen && a.decoys.some((d) => d.enabled && d.listening === false) && (
                <p className="honey-agente__errore">
                  {t('Porte non aperte (occupate?):')}{' '}
                  <span className="mono">
                    {a.decoys.filter((d) => d.enabled && d.listening === false)
                      .map((d) => `${d.service}/${d.port}`).join(', ')}
                  </span>
                </p>
              )}
              {a.stato === 'silenzioso' && (
                <p className="honey-agente__errore">
                  {t('L’agent non si fa più sentire: servizio fermo, o non raggiunge più il Control Plane.')}
                </p>
              )}
              {a.last_error && <p className="honey-agente__errore">{a.last_error}</p>}
              {modifica === a.id ? (
                <ModificaAgente agent={a} onFatto={() => { setModifica(null); onCambiato() }}
                                onAnnulla={() => setModifica(null)} />
              ) : (
                <div className="rowactions" style={{ textAlign: 'left' }}>
                  <button type="button" className="secondary dob-azione"
                          onClick={() => setModifica(a.id)}>{t('Modifica')}</button>
                  <button type="button" className="secondary dob-azione"
                          onClick={() => void rigenera(a.id)}>{t('Rigenera segreto')}</button>
                  {!a.revoked && (
                    <button type="button" className="secondary dob-azione"
                            onClick={() => void revoca(a.id)}>{t('Revoca')}</button>
                  )}
                  <button type="button" className="secondary dob-azione"
                          onClick={() => void elimina(a.id)}>{t('Elimina')}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ErrorBanner error={errore} />
    </Card>
  )
}

function ModificaAgente({ agent, onFatto, onAnnulla }: {
  agent: HoneypotAgent
  onFatto: () => void
  onAnnulla: () => void
}) {
  const [endpoint, setEndpoint] = useState(agent.endpoint || '')
  const [intervallo, setIntervallo] = useState(agent.interval_minutes)
  const [decoys, setDecoys] = useState<HoneypotDecoy[]>(agent.decoys)
  const [ignorati, setIgnorati] = useState((agent.ignored_sources || []).join(', '))
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  async function salva() {
    setBusy(true)
    setErrore(null)
    try {
      await api.modificaHoneypotAgent(agent.id, {
        endpoint: endpoint.trim() || null,
        interval_minutes: intervallo,
        decoys,
        ignored_sources: ignorati.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      })
      onFatto()
    } catch (e) { setErrore(e) } finally { setBusy(false) }
  }

  return (
    <div className="honey-modifica">
      <p className="field__hint">
        {t('Le modifiche valgono al prossimo file scaricato: rigenera il segreto per prendere il file aggiornato, o modifica il file sulla macchina a mano.')}
      </p>
      <div className="grid grid--2">
        <label className="field">
          <span className="field__label">{t('Consegna a')}</span>
          <SelettoreConsegna value={endpoint} onChange={setEndpoint} />
        </label>
        <label className="field">
          <span className="field__label">{t('Consegna ogni (minuti)')}</span>
          <input type="number" min={1} max={1440} value={intervallo}
                 onChange={(e) => setIntervallo(Number(e.target.value) || 5)} style={{ width: 100 }} />
        </label>
      </div>
      <TabellaEsche decoys={decoys} onCambia={setDecoys} />
      <label className="field" style={{ marginTop: 12 }}>
        <span className="field__label">{t('IP da ignorare (opzionale)')}</span>
        <textarea
          value={ignorati}
          onChange={(e) => setIgnorati(e.target.value)}
          rows={2}
          placeholder={t('es. 10.0.0.5, 192.168.1.0/24')}
        />
        <span className="field__hint">
          {t('Le connessioni da questi indirizzi non diventano hit: la sonda stessa, i sistemi di monitoraggio, gli scanner autorizzati. IP singoli o CIDR, separati da virgola o a capo.')}
        </span>
      </label>
      <div className="toolbar" style={{ marginTop: 12 }}>
        <button type="button" onClick={salva} disabled={busy}>{t('Salva')}</button>
        <button type="button" className="secondary" onClick={onAnnulla}>{t('Annulla')}</button>
      </div>
      <ErrorBanner error={errore} />
    </div>
  )
}

function StatoAgente({ stato }: { stato: string }) {
  if (stato === 'attivo') return <span className="badge badge--ok">{t('attivo')}</span>
  if (stato === 'silenzioso') return <span className="badge badge--warn">{t('silenzioso')}</span>
  if (stato === 'mai_visto') return <span className="badge badge--warn">{t('mai visto')}</span>
  if (stato === 'scaduto') return <span className="badge badge--error">{t('scaduto')}</span>
  return <span className="badge">{t('revocato')}</span>
}

// ------------------------------------------------- gli hit

function Hits({ hits, agents, onCambiato }: {
  hits: HoneypotHit[]
  agents: HoneypotAgent[]
  onCambiato: () => void
}) {
  const [errore, setErrore] = useState<unknown>(null)
  const [dettaglio, setDettaglio] = useState<HoneypotHit | null>(null)
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())
  const [lavoro, setLavoro] = useState(false)
  const nomeAgente = new Map(agents.map((a) => [a.id, a.name]))

  async function marca(ids: string[], stato: HoneypotHit['stato']) {
    setErrore(null)
    setLavoro(true)
    try {
      await Promise.all(ids.map((id) => api.setHoneypotHitState(id, stato)))
      setSelezionati(new Set())
      if (dettaglio && ids.includes(dettaglio.id)) setDettaglio({ ...dettaglio, stato })
      onCambiato()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const colonne: Column<HoneypotHit>[] = [
    {
      key: 'stato', header: t('Stato'), value: (h) => h.stato,
      render: (h) => <StatoBadge stato={h.stato} />,
    },
    {
      key: 'source', header: t('Origine'), value: (h) => h.source_address,
      render: (h) => <span className="mono">{h.source_address}</span>,
    },
    {
      key: 'service', header: t('Esca'), value: (h) => h.service,
      render: (h) => <span className="mono">{h.service.toUpperCase()}:{h.dst_port}</span>,
    },
    {
      key: 'agent', header: t('Agent'), nowrap: true,
      value: (h) => (h.agent_id ? nomeAgente.get(h.agent_id) || '' : ''),
      render: (h) => h.agent_id
        ? <span>{nomeAgente.get(h.agent_id) || '—'}</span>
        : <span className="muted">—</span>,
    },
    {
      key: 'occurred_at', header: t('Ultimo tentativo'), type: 'date',
      value: (h) => h.occurred_at,
      render: (h) => (h.occurred_at ? formatDateTime(h.occurred_at) : '—'),
    },
    {
      key: 'hits', header: t('Tentativi'), type: 'number', align: 'right',
      value: (h) => h.hits,
    },
    {
      key: 'credentials', header: t('Credenziali provate'),
      value: (h) => h.credentials,
      render: (h) => h.credentials ? <span className="mono">{h.credentials}</span> : <span className="muted">—</span>,
    },
    {
      key: 'payload', header: t('Payload'), value: (h) => h.payload,
      // One line, cut with «…»: the whole payload is in «Dettagli». A long
      // capture used to widen the table until the first columns scrolled away.
      render: (h) => h.payload
        ? <code className="honey-payload honey-payload--riga"
                title={t('Clic per il payload completo')}
                onClick={() => setDettaglio(h)}>{h.payload.slice(0, 200)}</code>
        : <span className="muted">—</span>,
    },
    {
      key: 'azioni', header: '', searchable: false, sort: false, value: () => '',
      render: (h) => (
        <button type="button" className="secondary dob-azione"
                onClick={(e) => { e.stopPropagation(); setDettaglio(h) }}>
          {t('Dettagli')}
        </button>
      ),
    },
  ]

  return (
    <Card title={`${t('Hit')} (${hits.length})`}>
      {/* Il payload catturato per intero: per SSH/RDP è il banner del client
          (es. la versione del software che sta bussando), per Telnet/HTTP i byte
          inviati. È «in caso poter vedere per capire»: qui, non troncato. */}
      {dettaglio && (
        <div className="banner" style={{ marginBottom: 12 }}>
          <div className="toolbar" style={{ justifyContent: 'space-between' }}>
            <strong>{t('Payload catturato')}</strong>
            <span className="toolbar">
              {dettaglio.stato === 'nuovo' && (
                <button type="button" className="secondary dob-azione" disabled={lavoro}
                        onClick={() => void marca([dettaglio.id], 'visto')}>{t('Segna visto')}</button>
              )}
              {dettaglio.stato !== 'archiviato' && (
                <button type="button" className="secondary dob-azione" disabled={lavoro}
                        onClick={() => void marca([dettaglio.id], 'archiviato')}>{t('Archivia')}</button>
              )}
              <button type="button" className="secondary dob-azione"
                      onClick={() => setDettaglio(null)}>{t('Chiudi')}</button>
            </span>
          </div>
          <div className="honey-agente__meta">
            <span>{t('Origine')}: <span className="mono">{dettaglio.source_address}</span></span>
            <span>{t('Esca')}: <span className="mono">{dettaglio.service.toUpperCase()}:{dettaglio.dst_port}</span></span>
            {dettaglio.credentials && (
              <span>{t('Credenziali provate')}: <span className="mono">{dettaglio.credentials}</span></span>
            )}
          </div>
          <pre className="mono" style={{ overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 320, padding: 12 }}>
            {dettaglio.payload || t('(vuoto: connessione senza dati inviati — spesso uno scanner di porte)')}
          </pre>
        </div>
      )}
      {hits.length === 0 ? (
        <EmptyState
          title={t('Nessun hit')}
          hint={t('Nessuno si è ancora connesso alle esche. Quando succede, comparirà qui con il payload catturato — e, se hai configurato le notifiche, arriverà una mail.')}
        />
      ) : (
        <>
          {selezionati.size > 0 && (
            <div className="selezione-barra">
              <strong>{t('{n} hit selezionati', { n: selezionati.size })}</strong>
              <button type="button" className="secondary" disabled={lavoro}
                      onClick={() => void marca([...selezionati], 'visto')}>{t('Segna visto')}</button>
              <button type="button" className="secondary" disabled={lavoro}
                      onClick={() => void marca([...selezionati], 'archiviato')}>{t('Archivia')}</button>
              <button type="button" className="link" onClick={() => setSelezionati(new Set())}>{t('Annulla la selezione')}</button>
            </div>
          )}
          <DataTable
            rows={hits}
            columns={colonne}
            rowKey={(h) => h.id}
            initialSort={{ key: 'occurred_at', desc: true }}
            conteggio={{ nome: t('hit') }}
            selezione={{ chiavi: selezionati, onCambia: setSelezionati, id: (h) => h.id }}
          />
        </>
      )}
      <p className="field__hint">
        {t('Un rilevamento indica una connessione a un servizio esca che per definizione è un evento sospetto. Utilizzare «Ispeziona» per analizzarne l’origine in Dobermann o ricercarla nei flussi per ricostruirne la provenienza.')}
      </p>
      <ErrorBanner error={errore} />
    </Card>
  )
}

function StatoBadge({ stato }: { stato: string }) {
  if (stato === 'nuovo') return <span className="badge badge--error">{t('nuovo')}</span>
  if (stato === 'visto') return <span className="badge badge--warn">{t('visto')}</span>
  return <span className="badge">{t('archiviato')}</span>
}

// ------------------------------------------------- le statistiche

function StatisticheHoneypot() {
  const [giorni, setGiorni] = useState(30)
  const dati = useAsync(() => api.honeypotStats(giorni), [giorni])

  const s = dati.data
  return (
    <Card title={t('Statistiche')}>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <label>
          {t('Periodo')}{' '}
          <select value={giorni} onChange={(e) => setGiorni(Number(e.target.value))}>
            <option value={7}>{t('7 giorni')}</option>
            <option value={30}>{t('30 giorni')}</option>
            <option value={90}>{t('90 giorni')}</option>
          </select>
        </label>
      </div>
      {dati.loading && !s ? <Spinner /> : dati.error ? <ErrorBanner error={dati.error} /> : s ? (
        <>
          <div className="scanstats__kpi">
            <StatCard label={t('Hit nel periodo')} value={s.summary.hits_window}
                      tone={s.summary.hits_window > 0 ? 'warn' : undefined} />
            <StatCard label={t('Hit (7g)')} value={s.summary.hits_7d} />
            <StatCard label={t('Con credenziali')} value={s.summary.with_credentials}
                      tone={s.summary.with_credentials > 0 ? 'error' : undefined} />
            <StatCard label={t('Indirizzi distinti')} value={s.summary.sources} />
          </div>
          <div className="field">
            <label>{t('Hit per giorno')}</label>
            <CountSeries points={s.series.map((p) => ({ date: p.date, value: p.hits }))}
                         tone="warn" label={t('Hit per giorno')}
                         emptyHint={t('Nessun hit nel periodo.')} />
          </div>
          <div className="scanstats__liste">
            <div className="field">
              <label>{t('Per servizio esca')}</label>
              <CategoryBars data={Object.entries(s.by_service).map(([k, v]) => ({ key: k, label: k.toUpperCase(), value: v }))}
                            emptyHint={t('Nessun hit nel periodo.')} />
            </div>
            <div className="field">
              <label>{t('Sorgenti più attive')}</label>
              {s.top_sources.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>{t('Nessuna sorgente nel periodo.')}</p>
              ) : (
                <ul className="scanstats__ul">
                  {s.top_sources.map((src) => (
                    <li key={src.address}>
                      <span className="mono">{src.address}</span>
                      <span className="scanstats__meta">{src.hits} {t('hit')}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </Card>
  )
}

// ------------------------------------------------- impostazioni e notifiche

function ImpostazioniNotificheHoneypot() {
  const dati = useAsync(() => api.honeypotNotifications(), [])
  const [email, setEmail] = useState<boolean | null>(null)
  const [soloCred, setSoloCred] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)

  const d = dati.data
  if (!d) return dati.error ? <Card title={t('Impostazioni e notifiche')}><ErrorBanner error={dati.error} /></Card> : null

  const vEmail = email ?? d.email
  const vSoloCred = soloCred ?? d.only_with_credentials

  async function salva() {
    setBusy(true); setErrore(null)
    try {
      await api.setHoneypotNotifications({ email: vEmail, only_with_credentials: vSoloCred })
      setEmail(null); setSoloCred(null)
      await dati.reload()
    } catch (e) { setErrore(e) } finally { setBusy(false) }
  }

  return (
    <Card title={t('Impostazioni e notifiche')}>
      <div className="field">
        <label className="check">
          <input type="checkbox" checked={vEmail} onChange={(e) => setEmail(e.target.checked)} />
          {t('Avvisami via email quando qualcuno si connette a un’esca')}
        </label>
        {vEmail && !d.recipients_configured && (
          <p className="field__hint">
            {t('Nessun destinatario configurato: aggiungilo in Impostazioni → Posta in uscita, o l’avviso non arriva a nessuno.')}
          </p>
        )}
      </div>
      <div className="field">
        <label className="check">
          <input type="checkbox" checked={vSoloCred} onChange={(e) => setSoloCred(e.target.checked)} />
          {t('Avvisa solo per gli hit che hanno catturato credenziali')}
        </label>
        <p className="field__hint">
          {t('Taglia il rumore dei port scan che si connettono e non inviano niente. L’hit resta comunque registrato: cambia solo se parte la mail.')}
        </p>
      </div>
      <div className="toolbar">
        <button type="button" onClick={salva} disabled={busy}>{t('Salva')}</button>
      </div>
      <ErrorBanner error={errore} />
    </Card>
  )
}
